<template>
    <DsInlineAlert
        v-if="displayAlert"
        :type="error ? 'critical' : 'warning'"
        class="inline-error-container-domain"
        data-qa="validation-error"
    >
        <template #default>
            <div v-if="showTestEmailSenderWarning">
                <p> {{ $t('email.unverified.domain.noneConnected.testEmail') }} </p>
            </div>

            <div v-else-if="error">
                <p> {{ $t('email.unverified.domain.error') }} </p>
            </div>

            <div v-else-if="warning">
                <p>
                    {{ $tc('email.unverified.domain.warning.alternativePreText', unverifiedOwnersCount, { unverifiedOwnersCount }) }}
                </p>
            </div>
        </template>

        <template #footer>
            <DsButton
                dense
                gray
                data-qa="dkim-alert-authenticate-domain-btn"
                @click="handleAddCustomDomain"
            >
                {{ authenticationButtonMessage }}
            </DsButton>

            <DsButton
                variant="text"
                dense
                gray
                as="a"
                :href="$options.DKIM_LEARN_MORE_LINK_ALL_EDITIONS"
                target="_blank"
                data-qa="dkim-alert-learn-more-btn"
            >
                {{ $t('global.learnMore') }}
            </DsButton>
        </template>
    </DsInlineAlert>
</template>

<script>
import { getFromAddress } from '@/components/broadcast/utils/emailBroadcast.util';
import {
    getVerifiedDomainsKeysOrDmarc,
    getVerifiedDomainsNewKeys,
    FILTERED_DOMAINS,
} from '@/components/settings/util/domains.util.js';
import { getCustomDomains } from '@/api/broadcasts/emailBroadcast.js';
import {
    DKIM_LEARN_MORE_LINK_ALL_EDITIONS,
    DKIM_SET_UP_LINK_NON_SPA,
} from '@/components/broadcast/constants/emailBroadcast.constants';

export default {
    DKIM_LEARN_MORE_LINK_ALL_EDITIONS,

    props: {
        senderList: {
            type: Array,
            default: () => [],
        },
        sender: {
            type: String,
            default: '',
        },
        contactOwnerIdentifier: {
            type: String,
            default: null,
        },
        environment: {
            type: String,
            default: 'Production',
        },
        isSpaApp: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        showTestEmailSenderWarning: Boolean,
    },

    data() {
        return {
            error: false,
            warning: false,
            addCustomEmailModalOpen: false,
            dkimDomains: [],
            loadingDomains: true,
        };
    },

    async created() {
        try {
            this.dkimDomains = await getCustomDomains();
        } catch (e) {
            this.$emit('error-loading-domains');
            // emailDomainValidity computed property won't update if we fail to fetch
            // domains and so the watcher on it won't be called as well so we need to
            // call setValidity in this case.
            this.setValidity();
        } finally {
            this.loadingDomains = false;
        }
    },

    watch: {
        emailDomainValidity() {
            if (this.loadingDomains) return;

            this.setValidity();
        },
    },

    computed: {
        verifiedDomains() {
            if (!this.showVerifiedDomainsNewKeysOnlyEnabled) {
                return getVerifiedDomainsKeysOrDmarc(this.dkimDomains);
            }

            return getVerifiedDomainsNewKeys(this.dkimDomains);
        },

        verifiedEmailDomainOptions() {
            return this.verifiedDomains.map((domain) => {
                return {
                    label: `@${domain}`,
                    value: `@${domain}`,
                };
            });
        },

        displayAlert() {
            return this.showTestEmailSenderWarning || this.error || this.warning;
        },

        authenticationButtonMessage() {
            return this.showTestEmailSenderWarning
                ? this.$t('email.unverified.domain.connectDomain')
                : this.$t('email.unverified.domain.authenticateDomain');
        },

        unverifiedOwnersCount() {
            return this.senderList.reduce((count, email) => {
                const isVerified = this.verifiedEmailDomainOptions.some(({ value: domainValue }) => email.toLowerCase().includes(domainValue.toLowerCase()));
                const isFreeDomain = FILTERED_DOMAINS.includes(email.toLowerCase().split('@').pop());
                const isContactOwner = email.includes(this.contactOwnerIdentifier);

                return ((isVerified && !isContactOwner) || isFreeDomain) ? count : count + 1;
            }, -1);
        },

        emailDomainValidity() {
            const validity = {
                error: false,
                warning: false,
            };

            if (!this.sender) {
                return validity;
            }

            if (this.sender === this.contactOwnerIdentifier) {
                validity.warning = (this.unverifiedOwnersCount > 0);
            } else {
                const fromAddress = getFromAddress(this.sender);
                const isEmailFromValidDomain = this.verifiedEmailDomainOptions.some(({ value: domainValue }) => fromAddress.toLowerCase().includes(domainValue.toLowerCase()));

                validity.error = !isEmailFromValidDomain;
            }

            return validity;
        },
    },

    methods: {
        setValidity() {
            this.error = this.emailDomainValidity.error;
            this.warning = this.emailDomainValidity.warning;

            this.$emit('validity', this.emailDomainValidity);
        },

        handleAddCustomDomain() {
            const url = (() => {
                if (this.isSpaApp) {
                    const path = '/settings/domains';

                    switch (this.environment.toLowerCase()) {
                    case 'production':
                    default:
                        return `https://keap.app${path}`;
                    case 'development':
                    case 'sandbox':
                        return `http://localhost:10239${path}`;
                    case 'integration':
                        return `https://integration.keap.app${path}`;
                    case 'proofing':
                    case 'staging':
                        return `https://staging.keap.app${path}`;
                    }
                }

                return DKIM_SET_UP_LINK_NON_SPA;
            })();

            window.open(url, '_blank');
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.inline-error-container-domain {
    margin-bottom: $spacing-150;
}
</style>
